import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ChevronDown, Search, X } from "lucide-react";
import { Seo } from "@/components/Seo";
import { ProductCard } from "@/components/ProductCard";
import { Loader } from "@/components/brand/Mascot";
import { MascotDoodle } from "@/components/brand/MascotDoodle";
import { getBundles, getProducts } from "@/lib/api";
import { BundleTile, GiftTile } from "@/components/ShopExtras";
import { CATEGORIES, breadcrumb } from "@/lib/site";

const SORTS = [["featured", "Featured"], ["price-asc", "Price: low to high"], ["price-desc", "Price: high to low"], ["newest", "Newest"]];

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const category = params.get("category") || "all";
  const sort = params.get("sort") || "featured";
  const q = params.get("q") || "";
  const drop = params.get("drop") || "";
  const [products, setProducts] = useState(null);
  const [bundles, setBundles] = useState([]);
  const extras = category === "all" && !q && !drop;

  useEffect(() => { getBundles().then(setBundles).catch(() => {}); }, []);
  useEffect(() => {
    if (category === "bundles") return setProducts([]);
    getProducts({ category, sort, q: q || undefined, drop: drop || undefined }).then(setProducts).catch(() => setProducts([]));
  }, [category, sort, q, drop]);

  const update = (key, val) => {
    const next = new URLSearchParams(params);
    if (!val || val === "all" || (key === "sort" && val === "featured")) next.delete(key);
    else next.set(key, val);
    setParams(next, { replace: true });
  };

  const catLabel = CATEGORIES.find((c) => c.id === category)?.label || "All";
  return (
    <div className="mx-auto max-w-[1440px] px-5 sm:px-10" data-testid="shop-page">
      <Seo title={category === "all" ? "Shop all streetwear" : `Shop ${catLabel}`} description="Shop the suspects: heavyweight tees from C$48, hoodies from C$88, crewnecks, caps, beanies and totes. Toronto streetwear in CAD with free shipping over C$100." path="/shop" jsonLd={[breadcrumb([{ name: "Home", path: "/" }, { name: "Shop", path: "/shop" }])]} />
      <header className="grid items-end gap-6 pb-12 pt-16 lg:grid-cols-12 lg:pt-24">
        <h1 className="display text-[clamp(4rem,10vw,8.75rem)] lg:col-span-8">Shop all</h1>
        <p className="max-w-sm text-[15px] leading-relaxed text-ink/70 lg:col-span-4 lg:justify-self-end lg:text-right">Tees, hoodies, crewnecks and more. Designed for the curious kind.</p>
      </header>

      <div className="sticky top-[88px] z-30 -mx-5 border-y border-ink/10 bg-cloud px-5 py-4 sm:-mx-10 sm:px-10 lg:top-32">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="no-scrollbar -mx-1 flex gap-2 overflow-x-auto px-1" role="group" aria-label="Filter by category">
            {CATEGORIES.map((c) => (
              <button key={c.id} onClick={() => update("category", c.id)} aria-pressed={category === c.id} className={`label h-9 shrink-0 rounded-full border-[1.5px] border-ink px-5 font-bold transition-colors ${category === c.id ? "bg-ink text-cloud" : "hover:bg-ink/5"}`} data-testid={`category-chip-${c.id}`}>
                {c.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-6">
            <div className="relative flex-1 border-b border-ink/30 lg:w-56 lg:flex-none">
              <Search size={14} strokeWidth={1.5} className="absolute left-0 top-1/2 -translate-y-1/2" aria-hidden="true" />
              <label htmlFor="shop-search" className="sr-only">Search products</label>
              <input id="shop-search" value={q} onChange={(e) => update("q", e.target.value)} placeholder="Search" className="h-9 w-full bg-transparent pl-6 pr-6 text-sm placeholder:text-ink/40 focus:outline-none" data-testid="search-input" />
              {q && <button onClick={() => update("q", "")} className="absolute right-0 top-1/2 -translate-y-1/2" aria-label="Clear search" data-testid="search-clear"><X size={14} /></button>}
            </div>
            <label htmlFor="sort" className="relative flex shrink-0 items-center gap-1">
              <span className="label text-ink/55">Sort by:</span>
              <select id="sort" value={sort} onChange={(e) => update("sort", e.target.value)} className="label cursor-pointer appearance-none bg-transparent pr-5 font-bold focus:outline-none" data-testid="sort-select">
                {SORTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
              <ChevronDown size={14} className="pointer-events-none absolute right-0" aria-hidden="true" />
            </label>
          </div>
        </div>
        {drop && (
          <button onClick={() => update("drop", "")} className="label mt-3 inline-flex items-center gap-2 font-bold" data-testid="drop-filter-clear">Drop: {drop.replace(/-/g, " ")} <X size={12} /></button>
        )}
      </div>

      {products === null ? <Loader label="Loading products" /> : (
        <div className="pb-28">
          <p className="label mt-8 text-ink/50" data-testid="shop-result-count">{category === "bundles" ? `${bundles.length + 1} bundles & gift cards` : `${products.length} ${products.length === 1 ? "product" : "products"}`}{q && ` matching "${q}"`}</p>
          {category === "bundles" ? (
            <div className="mt-8 grid grid-cols-2 gap-x-4 gap-y-12 sm:gap-x-6 lg:grid-cols-4" data-testid="shop-bundles-grid">{bundles.map((b) => <BundleTile key={b.slug} b={b} />)}<GiftTile /></div>
          ) : products.length === 0 ? (
            <div className="flex flex-col items-center gap-5 py-28 text-center" data-testid="shop-empty">
              <MascotDoodle variant="tongue" size={130} />
              <p className="text-lg">No suspects here. Try another search.</p>
            </div>
          ) : (
            <div className="mt-8 grid grid-cols-2 gap-x-4 gap-y-12 sm:gap-x-6 lg:grid-cols-4" data-testid="shop-grid">
              {products.map((p, i) => <ProductCard key={p.slug} product={p} index={i} />)}
              {extras && bundles.map((b) => <BundleTile key={b.slug} b={b} />)}
              {extras && <GiftTile />}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
